/**
 * Drupal Batch API modal.
 *
 * When the navigation engine intercepts a form submission that
 * redirects to `/batch?op=start&id=N`, it hands the batch URL
 * off to this widget instead of letting the browser load the
 * default batch page. The widget:
 *
 * 1. Builds a minimal progress modal imperatively.
 * 2. Delegates polling to BatchPoller from @sdx/core/batch-polling,
 *    the single source of truth for the Drupal Batch protocol
 *    used by every SDX poller (navigate form intercept, React
 *    useConfirmModal, future Vue/Svelte adapters).
 * 3. Maps each progress tick into the modal UI via onProgress.
 * 4. On finish hands control back to the navigation controller
 *    through the continuation callback so the user ends up
 *    SPA-navigated to the original destination.
 *
 * Drupal batch messages are server-produced trusted HTML - the
 * core Drupal.behaviors.batch block writes them the same way
 * via innerHTML - so the `innerHTML` assignment here follows
 * established core precedent, not new XSS surface.
 */

import { BatchPoller } from '@sdx/core/batch-polling';
import { t } from '@sdx/i18n';

const FINISH_DISMISS_MS = 400;

const STYLE_TAG_ID = 'sdx-batch-styles';
const INLINE_STYLES = [
  '.sdx-batch-overlay{position:fixed;inset:0;z-index:10000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.3);backdrop-filter:blur(2px)}',
  '.sdx-batch-modal{background:#fff;border-radius:12px;padding:24px 32px;min-width:320px;max-width:480px;box-shadow:0 8px 32px rgba(0,0,0,.15)}',
  '.sdx-batch-modal__title{font-size:15px;font-weight:600;margin-bottom:12px;color:#1e293b}',
  '.sdx-batch-modal__bar{height:6px;background:#e2e8f0;border-radius:3px;overflow:hidden;margin-bottom:8px}',
  '.sdx-batch-modal__fill{height:100%;background:#6366f1;border-radius:3px;transition:width .3s ease;width:0}',
  '.sdx-batch-modal__message{font-size:12px;color:#64748b}',
].join('');

export type BatchFinishedCallback = (destinationUrl: string) => void;

export class BatchModal {
  private readonly overlay: HTMLDivElement;
  private readonly titleEl: HTMLDivElement;
  private readonly fill: HTMLDivElement;
  private readonly messageEl: HTMLDivElement;

  constructor() {
    ensureInlineStyles();
    this.overlay = document.createElement('div');
    this.overlay.className = 'sdx-batch-overlay';
    const modal = document.createElement('div');
    modal.className = 'sdx-batch-modal';
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'sdx-batch-modal__title';
    this.titleEl.textContent = t('Processing...');
    const barWrap = document.createElement('div');
    barWrap.className = 'sdx-batch-modal__bar';
    this.fill = document.createElement('div');
    this.fill.className = 'sdx-batch-modal__fill';
    barWrap.appendChild(this.fill);
    this.messageEl = document.createElement('div');
    this.messageEl.className = 'sdx-batch-modal__message';
    modal.append(this.titleEl, barWrap, this.messageEl);
    this.overlay.appendChild(modal);
  }

  /**
   * Mounts the modal, drives the batch protocol to completion
   * via BatchPoller, and hands the destination URL back through
   * `onFinished` so the navigation controller can SPA-navigate
   * to it.
   */
  run(batchUrl: string, returnUrl: string, onFinished: BatchFinishedCallback): void {
    document.body.appendChild(this.overlay);

    const poller = new BatchPoller({
      batchUrl,
      onProgress: (progress) => {
        this.fill.style.width = `${progress.percentage}%`;
        if (progress.message) {
          this.messageEl.innerHTML = progress.message;
        }
        if (progress.label) {
          this.titleEl.innerHTML = progress.label;
        }
      },
      onFinished: () => this.finish(returnUrl, onFinished),
    });
    poller.run();
  }

  private finish(destinationUrl: string, onFinished: BatchFinishedCallback): void {
    this.fill.style.width = '100%';
    this.titleEl.textContent = t('Complete');
    // Brief UX hold so the user sees the completed state before
    // the overlay disappears.
    setTimeout(() => {
      this.overlay.remove();
      onFinished(destinationUrl);
    }, FINISH_DISMISS_MS);
  }
}

function ensureInlineStyles(): void {
  if (document.getElementById(STYLE_TAG_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_TAG_ID;
  style.textContent = INLINE_STYLES;
  document.head.appendChild(style);
}
